"use client";

import { Paperclip, Send, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/ui/loader";
import { Textarea } from "@/components/ui/textarea";
import { useAIBuilder } from "@/hooks/ai-builder/use-ai-builder";
import type { FormSchema } from "@/lib/database";
import {
	RECOGNITION_ACCEPT,
	RECOGNITION_ALLOWED_TYPES,
	RECOGNITION_MAX_FILE_SIZE,
} from "@/lib/recognition/document-types";
import { PromptFieldGhosts } from "./prompt-field-ghosts";
import { type PromptPhase, TypewriterExamples } from "./typewriter-examples";

interface DemoAIGeneratorProps {
	/** Схема готова: главная переключается в область ручного редактирования. */
	onGenerated: (schema: FormSchema) => void;
	/** Идёт генерация: главная скрывает чат и показывает загрузку демо-конструктора. */
	onGeneratingChange?: (isGenerating: boolean) => void;
	/** Дневной лимит исчерпан: показать окно регистрации либо ручного редактирования. */
	onLimitReached: () => void;
}

/**
 * ИИ-генератор форм на главной (t_4c03e049, переделка t_1c710027).
 * Не чат: заголовок, анимированные примеры запросов, одно поле ввода, отправка и загрузка файла.
 * Текст заголовка — прежний текст hero-блока главной, это единственный h1 страницы.
 * Под полем вылетают три прямоугольника-имитации сгенерированных полей.
 * Генерацию делает общий пайплайн билдера в демо-режиме (лимиты вместо премиума).
 */
export function DemoAIGenerator({
	onGenerated,
	onGeneratingChange,
	onLimitReached,
}: DemoAIGeneratorProps) {
	const t = useTranslations("aiBuilder");
	const tDemo = useTranslations("home.demoAi");
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);
	const [fileError, setFileError] = useState<string | null>(null);
	const [limitNotice, setLimitNotice] = useState<string | null>(null);
	// Фаза анимации примеров: текст живёт над полем, «призрачные» поля — под ним
	const [promptPhase, setPromptPhase] = useState<PromptPhase>("typing");

	const handlePromptPhase = useCallback((next: PromptPhase) => {
		setPromptPhase(next);
	}, []);

	const handleLimitReached = useCallback(
		(kind: "anon" | "user") => {
			setLimitNotice(
				kind === "anon" ? tDemo("limit.anon") : tDemo("limit.user")
			);
			onLimitReached();
		},
		[onLimitReached, tDemo]
	);

	const {
		input,
		isLoading,
		isStreaming,
		activeForm,
		attachedFile,
		setAttachedFile,
		setInput,
		handleSend,
	} = useAIBuilder(undefined, {
		demo: true,
		onDemoLimitReached: handleLimitReached,
	});

	const examples = useMemo(
		() => [
			t("suggestions.contactForm"),
			t("suggestions.feedbackSurvey"),
			t("suggestions.eventRegistration"),
			t("suggestions.jobApplication"),
		],
		[t]
	);

	// Схема получена — отдаём её наверх, в область ручного редактирования
	useEffect(() => {
		if (activeForm?.schema) {
			onGenerated(activeForm.schema);
		}
	}, [activeForm?.schema, onGenerated]);

	const isGenerating = isLoading || isStreaming;
	const canSubmit = Boolean(input.trim() || attachedFile);
	// Пульсация вокруг поля — только пока запрос не отправлен (то самое «окно ввода ждёт запроса»)
	const isIdle = !(isGenerating || limitNotice);

	// Сообщаем наверх, что идёт генерация: главная по этому флагу скрывает чат
	useEffect(() => {
		onGeneratingChange?.(isGenerating);
	}, [isGenerating, onGeneratingChange]);

	// Поле растёт под текст, поэтому длинный запрос не обрезается (мобильная вёрстка)
	const autoResize = () => {
		const el = textareaRef.current;
		if (!el) {
			return;
		}
		el.style.height = "auto";
		el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
	};

	const handleFilePick = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] || null;
		event.target.value = "";
		if (!file) {
			return;
		}
		if (!RECOGNITION_ALLOWED_TYPES.includes(file.type)) {
			setFileError(t("errors.fileType"));
			return;
		}
		if (file.size > RECOGNITION_MAX_FILE_SIZE) {
			setFileError(t("errors.fileTooLarge"));
			return;
		}
		setFileError(null);
		setAttachedFile(file);
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (isGenerating || !canSubmit) {
			return;
		}
		setLimitNotice(null);
		setFileError(null);
		handleSend(event);
	};

	return (
		<section className="flex w-full flex-col gap-6 bg-card px-4 pt-4 pb-10 md:px-8">
			{/* Заголовок страницы живёт в hero.tsx: он остаётся видимым и при демо-конструкторе */}
			<div className="flex flex-col items-center text-center">
				{/* Пока идёт генерация, строка примеров становится строкой статуса: высота та же
				    (min-h-6), поэтому блок ниже не смещается (t_1c710027). */}
				{isGenerating ? (
					<div className="flex min-h-6 items-center justify-center gap-2 text-muted-foreground text-sm">
						<Loader />
						<span>{tDemo("loading")}</span>
					</div>
				) : (
					<TypewriterExamples
						examples={examples}
						onPhaseChange={handlePromptPhase}
					/>
				)}
			</div>

			<form
				className="mx-auto flex w-full max-w-3xl flex-col gap-3"
				onSubmit={handleSubmit}
			>
				<div className="relative z-10 w-full">
					{isIdle && (
						<div
							aria-hidden="true"
							className="pointer-events-none absolute -inset-1.5 animate-[pulse_3.4s_ease-in-out_infinite] rounded-2xl bg-primary/20 blur-md motion-reduce:animate-none"
							data-slot="demo-generator-pulse"
						/>
					)}
					<div className="relative flex items-start gap-2 rounded-xl border border-border bg-background p-2 transition-all focus-within:border-primary/30">
						<input
							accept={RECOGNITION_ACCEPT}
							aria-label={t("input.attach")}
							className="hidden"
							onChange={handleFilePick}
							ref={fileInputRef}
							type="file"
						/>
						<Button
							aria-label={t("input.attach")}
							className="size-11"
							disabled={isGenerating}
							onClick={() => fileInputRef.current?.click()}
							size="icon"
							title={t("input.attach")}
							type="button"
							variant="ghost"
						>
							<Paperclip className="size-4" />
						</Button>
						<Textarea
							aria-label={t("input.label")}
							className="max-h-40 min-h-11 flex-1 resize-none border-none bg-transparent py-3 shadow-none outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
							disabled={isGenerating}
							id="demo-ai-prompt"
							onChange={(event) => {
								setInput(event.target.value);
								autoResize();
							}}
							onKeyDown={(event) => {
								if (event.key === "Enter" && !event.shiftKey) {
									event.preventDefault();
									event.currentTarget.form?.requestSubmit();
								}
							}}
							placeholder={tDemo("placeholder")}
							ref={textareaRef}
							rows={1}
							value={input}
						/>
						<Button
							aria-label={tDemo("submit")}
							className="size-11"
							disabled={isGenerating || !canSubmit}
							loading={isGenerating}
							size="icon"
							title={tDemo("submit")}
							type="submit"
						>
							{isGenerating ? null : <Send className="size-4" />}
						</Button>
					</div>
				</div>

				{/* Во время генерации «призрачные» поля не показываем: анимация не должна мешать */}
				<PromptFieldGhosts phase={isGenerating ? "typing" : promptPhase} />

				{attachedFile && (
					<div className="flex flex-wrap items-center gap-2 text-muted-foreground text-xs">
						<Badge className="gap-1" variant="secondary">
							<Paperclip aria-hidden="true" className="size-3" />
							{attachedFile.name}
							<button
								aria-label={t("input.removeFile")}
								className="ml-1 hover:text-destructive"
								onClick={() => setAttachedFile(null)}
								type="button"
							>
								<X aria-hidden="true" className="size-3" />
							</button>
						</Badge>
						<span>{t("input.attachHint")}</span>
					</div>
				)}

				{fileError && <p className="text-destructive text-xs">{fileError}</p>}
				{limitNotice && (
					<p className="text-destructive text-sm">{limitNotice}</p>
				)}
			</form>
		</section>
	);
}
